import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Copy, Check, Link2, Link2Off, Globe, BarChart3, Trash2, Lock, Clock, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SharePresentationDialog — create/revoke a public share link + view analytics.
 * Props:
 *   open, onOpenChange, projectId, projectName
 */
export default function SharePresentationDialog({ open, onOpenChange, projectId, projectName }) {
  const [state, setState] = useState(null);       // { is_shared, slug, url, view_count, unique_ips, has_password, expires_at, custom_title }
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Rich settings form state
  const [customTitle, setCustomTitle] = useState("");
  const [passwordDraft, setPasswordDraft] = useState("");
  const [expiryDraft, setExpiryDraft] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const auth = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/share`, { headers: auth() });
      setState(res.data);
      if (res.data?.is_shared) {
        setCustomTitle(res.data.custom_title || "");
        // Show yyyy-mm-dd in local input; empty if unset
        setExpiryDraft(res.data.expires_at ? res.data.expires_at.slice(0, 16) : "");
        setPasswordDraft(""); // never prefill password (we don't get it back)
        try {
          const s = await axios.get(`${API}/projects/${projectId}/share/stats`, { headers: auth() });
          setStats(s.data);
        } catch (_e) { setStats(null); }
      } else {
        setStats(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load share state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, projectId]);

  const create = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/share`, {}, { headers: auth() });
      setState({ ...res.data, is_shared: true });
      toast.success("Share link created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create link");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/projects/${projectId}/share`, { headers: auth() });
      setState({ is_shared: false });
      setStats(null);
      toast.success("Share link revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke");
    } finally {
      setLoading(false);
    }
  };

  const fullUrl = state?.slug ? `${window.location.origin}/s/${state.slug}` : "";
  const embedUrl = state?.slug ? `${window.location.origin}/embed/s/${state.slug}?autoplay=8&loop=1` : "";
  const embedSnippet = state?.slug
    ? `<iframe src="${embedUrl}" width="800" height="450" frameborder="0" allowfullscreen></iframe>`
    : "";

  const copy = () => {
    if (!fullUrl) return;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const body = {
        custom_title: customTitle,
      };
      // Only send password if the user typed one (leave server value alone otherwise).
      if (passwordDraft) body.password = passwordDraft;
      // Convert local datetime to ISO if provided.
      if (expiryDraft) {
        const iso = new Date(expiryDraft).toISOString();
        body.expires_at = iso;
      } else {
        body.expires_at = ""; // clear
      }
      await axios.patch(`${API}/projects/${projectId}/share`, body, { headers: auth() });
      toast.success("Share settings saved");
      setPasswordDraft("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const clearPassword = async () => {
    try {
      await axios.patch(`${API}/projects/${projectId}/share`, { password: "" }, { headers: auth() });
      toast.success("Password removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-amber-500" />
            Share &ldquo;{projectName || "this deck"}&rdquo;
          </DialogTitle>
          <DialogDescription>
            Anyone with the link can view — no account needed. You can revoke access anytime.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
          </div>
        ) : state?.is_shared ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 truncate" data-testid="share-link-url">
                {fullUrl}
              </div>
              <Button variant="outline" className="border-slate-700" onClick={copy} data-testid="share-copy-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            {/* Badges for password/expiry */}
            <div className="flex flex-wrap gap-2">
              {state?.has_password && (
                <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid="share-badge-password">
                  <Lock className="w-3 h-3 mr-1" /> Password protected
                </Badge>
              )}
              {state?.expires_at && (
                <Badge variant="outline" className="border-slate-700 text-slate-300" data-testid="share-badge-expiry">
                  <Clock className="w-3 h-3 mr-1" /> Expires {new Date(state.expires_at).toLocaleDateString()}
                </Badge>
              )}
              {state?.custom_title && (
                <Badge variant="outline" className="border-slate-700 text-slate-300">
                  <Sparkles className="w-3 h-3 mr-1" /> {state.custom_title}
                </Badge>
              )}
            </div>

            {/* Settings accordion */}
            <Accordion type="single" collapsible className="border border-slate-800 rounded-lg">
              <AccordionItem value="settings" className="border-0">
                <AccordionTrigger className="px-4 py-3 hover:no-underline text-sm" data-testid="share-settings-toggle">
                  Link settings
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-3">                  <div>
                    <Label className="text-xs text-slate-400">Custom title (shown on rich previews)</Label>
                    <Input
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="Sunday sermon — Grace"
                      className="bg-slate-950 border-slate-800 mt-1"
                      data-testid="share-custom-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Password (optional)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="password"
                        value={passwordDraft}
                        onChange={(e) => setPasswordDraft(e.target.value)}
                        placeholder={state?.has_password ? "•••• (set — enter new to change)" : "Leave empty for no password"}
                        className="bg-slate-950 border-slate-800"
                        data-testid="share-password-input"
                      />
                      {state?.has_password && (
                        <Button variant="outline" onClick={clearPassword} className="border-slate-700 text-slate-300" data-testid="share-password-clear-btn">
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Expires on</Label>
                    <Input
                      type="datetime-local"
                      value={expiryDraft}
                      onChange={(e) => setExpiryDraft(e.target.value)}
                      className="bg-slate-950 border-slate-800 mt-1"
                      data-testid="share-expiry-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Leave empty for no expiry.</p>
                  </div>
                  <Button
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="bg-amber-500 text-black hover:bg-amber-400 w-full"
                    data-testid="share-save-settings-btn"
                  >
                    {savingSettings ? "Saving…" : "Save settings"}
                  </Button>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="embed" className="border-t border-slate-800">
                <AccordionTrigger className="px-4 py-3 hover:no-underline text-sm" data-testid="share-embed-toggle">
                  Embed anywhere
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-3">
                  <p className="text-xs text-slate-400">
                    Paste this into any website, Notion page, or blog post. Slides auto-advance every 8 seconds and loop.
                  </p>
                  <textarea
                    readOnly
                    value={embedSnippet}
                    className="w-full text-xs font-mono bg-slate-950 border border-slate-800 rounded-md p-3 h-24 text-slate-300 resize-none"
                    data-testid="share-embed-snippet"
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(embedSnippet);
                        setEmbedCopied(true);
                        toast.success("Embed code copied");
                        setTimeout(() => setEmbedCopied(false), 2000);
                      }}
                      className="bg-amber-500 text-black hover:bg-amber-400 flex-1"
                      data-testid="share-embed-copy-btn"
                    >
                      {embedCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {embedCopied ? "Copied" : "Copy embed code"}
                    </Button>
                    <Button
                      variant="outline"
                      className="border-slate-700"
                      onClick={() => window.open(embedUrl, "_blank")}
                      data-testid="share-embed-preview-btn"
                    >
                      Preview
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Tune with query params: <code className="text-slate-400">?autoplay=&lt;seconds&gt;</code>,{" "}
                    <code className="text-slate-400">&amp;loop=1</code>,{" "}
                    <code className="text-slate-400">&amp;bg=%23000</code>
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {(
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3 border border-slate-800 bg-slate-950/40" data-testid="share-stat-views">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <BarChart3 className="w-3 h-3" /> Total views
                  </div>
                  <div className="text-2xl font-semibold text-white mt-1">{stats?.view_count || 0}</div>
                </div>
                <div className="rounded-lg p-3 border border-slate-800 bg-slate-950/40" data-testid="share-stat-unique">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Globe className="w-3 h-3" /> Unique viewers
                  </div>
                  <div className="text-2xl font-semibold text-white mt-1">{stats?.unique_ips || 0}</div>
                </div>
              </div>
            )}

            {stats?.top_countries?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Top countries</div>
                <div className="flex flex-wrap gap-2">
                  {stats.top_countries.map((c) => (
                    <Badge key={c.country} variant="outline" className="border-slate-700 text-slate-300">
                      {c.country} · {c.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {stats?.top_referrers?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Top referrers</div>
                <div className="space-y-1">
                  {stats.top_referrers.map((r) => (
                    <div key={r.referrer} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300 truncate max-w-[280px]">{r.referrer}</span>
                      <span className="text-slate-500 text-xs">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-slate-800 flex justify-end">
              <Button
                variant="destructive"
                onClick={revoke}
                className="bg-rose-600 hover:bg-rose-500"
                data-testid="share-revoke-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Revoke link
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <Link2Off className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <div className="text-slate-300 mb-4">No share link yet.</div>
            <Button
              onClick={create}
              className="bg-amber-500 text-black hover:bg-amber-400"
              data-testid="share-create-btn"
            >
              Create share link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
